import type { CSSProperties } from "react";
import type { DashboardShellTheme } from "./types";

export const greenerBrandColors = {
  lightGreen: "#8CC63F",
  green: "#0B7A3D",
  darkBlue: "#0B3C60",
  blue: "#034F7B",
  purple: "#301D5C",
} as const;

export const appAccentExamples = {
  buildingAutomation: "#39036D",
  bluegill: "#1C3969",
} as const;

export const defaultTheme: Required<DashboardShellTheme> = {
  topNavBackground: "#ffffff",
  topNavText: "#1f2937",
  sideNavBackground: greenerBrandColors.green,
  sideNavActiveBackground: "rgba(255, 255, 255, 0.22)",
  sideNavActiveText: "#ffffff",
  sideNavHoverBackground: "rgba(255, 255, 255, 0.14)",
  accent: greenerBrandColors.green,
  border: "#d1d5db",
  logoAreaBackground: "#d9d9d9",
  appSwitcherBackground: "#ffffff",
  appSwitcherHoverBackground: "#f3f8f5",
  appBackground: "#ffffff",
  surface: "#ffffff",
  surfaceMuted: "#f8fafc",
  text: "#0f172a",
  textMuted: "#64748b",
  cardBackground: "#ffffff",
  cardText: "#0f172a",
  darkBackground: "#303030",
  darkSurface: "#3a3a3a",
  darkSurfaceMuted: "#454545",
  darkText: "#f8fafc",
  darkTextMuted: "#cbd5e1",
  darkBorder: "rgba(255, 255, 255, 0.16)",
  darkCardBackground: "#3a3a3a",
  darkCardText: "#f8fafc",
};

export function resolveTheme(theme: DashboardShellTheme = {}): Required<DashboardShellTheme> {
  return { ...defaultTheme, ...theme };
}

export function themeStyle(theme: DashboardShellTheme = {}, darkMode = false): CSSProperties {
  const resolved = resolveTheme(theme);
  return {
    "--in-app-top-bg": resolved.topNavBackground,
    "--in-app-top-text": resolved.topNavText,
    "--in-app-side-bg": resolved.sideNavBackground,
    "--in-app-side-active-bg": resolved.sideNavActiveBackground,
    "--in-app-side-active-text": resolved.sideNavActiveText,
    "--in-app-side-hover-bg": resolved.sideNavHoverBackground,
    "--in-app-accent": resolved.accent,
    "--in-app-border": darkMode ? resolved.darkBorder : resolved.border,
    "--in-app-logo-bg": resolved.logoAreaBackground,
    "--in-app-switcher-bg": darkMode ? resolved.darkSurface : resolved.appSwitcherBackground,
    "--in-app-switcher-hover-bg": darkMode ? "rgba(255, 255, 255, 0.08)" : resolved.appSwitcherHoverBackground,
    "--app-bg": darkMode ? resolved.darkBackground : resolved.appBackground,
    "--app-surface": darkMode ? resolved.darkSurface : resolved.surface,
    "--app-surface-muted": darkMode ? resolved.darkSurfaceMuted : resolved.surfaceMuted,
    "--app-text": darkMode ? resolved.darkText : resolved.text,
    "--app-text-muted": darkMode ? resolved.darkTextMuted : resolved.textMuted,
    "--app-border": darkMode ? resolved.darkBorder : resolved.border,
    "--app-card-bg": darkMode ? resolved.darkCardBackground : resolved.cardBackground,
    "--app-card-text": darkMode ? resolved.darkCardText : resolved.cardText,
    "--app-dark-bg": resolved.darkBackground,
    "--app-dark-surface": resolved.darkSurface,
    "--app-dark-surface-muted": resolved.darkSurfaceMuted,
    "--app-dark-text": resolved.darkText,
    "--app-dark-text-muted": resolved.darkTextMuted,
    "--app-dark-border": resolved.darkBorder,
    "--app-dark-card-bg": resolved.darkCardBackground,
    "--app-dark-card-text": resolved.darkCardText,
  } as CSSProperties;
}
